import Upsertable from '@/models/Upsertable'
import { useSurveyResponseStore } from '@/stores/surveyResponseStore'
import SurveyResponseAnswer from './SurveyResponseAnswer'
import Survey from './Survey'
import SurveyQuestion from './SurveyQuestion'
import AgriSubContract from './AgriSubContract'
import Customer from './Customer'
import Field from './Field'
import { useModelName } from '@/composables/useModelName.js'
import { DateTime } from 'luxon'
import { ModelRegistry } from '@/models/modelRegistry.js'

export default class SurveyResponse extends Upsertable {
  static store() {
    return useSurveyResponseStore()
  }

  store() {
    return SurveyResponse.store()
  }

  static all() {
    return SurveyResponse.store().all()
  }

  static find(uuid) {
    const obj = SurveyResponse.store().find(uuid)
    let survey_response = new SurveyResponse()
    return Object.assign(survey_response, obj)
  }

  static find_by_completed(uuid) {
    const obj = SurveyResponse.store().all().find(response => response.completed_by_uuid == uuid)
    let survey_response = new SurveyResponse()
    return Object.assign(survey_response, obj)
  }

  static find_by_week(uuid) {
    // Use Monday-Sunday week boundaries to match backend (Time.now.beginning_of_week to Time.now.end_of_week)
    const now = DateTime.now()
    const startOfWeek = now.startOf('week').startOf('day')  // Monday
    const endOfWeek = now.endOf('week').endOf('day')        // Sunday

    return SurveyResponse.store()
      .all()
      .filter(sr => {
        // created_at can be a number (milliseconds) or ISO string
        let createdAt
        if (typeof sr.created_at === 'number') {
          createdAt = DateTime.fromMillis(sr.created_at)
        } else {
          createdAt = DateTime.fromISO(sr.created_at)
        }
        
        if (!createdAt.isValid) return false
        
        return (
          sr.survey_uuid === uuid &&
          createdAt >= startOfWeek &&
          createdAt <= endOfWeek
        )
      })
  }

  static findAllSurveyResponsesByContractUuid(uuid) {
    return SurveyResponse.store().all().find(response => response.contract_uuid == uuid)
  }

  static findAllSurveyResponsesByFieldUuid(fieldUuid) {
    const contracts = AgriSubContract.store().all().filter(c => c.field_uuid === fieldUuid)
    const contractUuids = contracts.map(c => c.uuid)
    
    return SurveyResponse.store().all().filter(response => 
      contractUuids.includes(response.contract_uuid)
    )
  }

  /**
   * Same notion of “unanswered” as {@link SurveyResponse.completed}: `null`,
   * blank/whitespace-only strings, and empty arrays are empty. Numbers (including `0`) count as answered.
   */
  static isEmptyAnswerValue(value) {
    if (value == null) return true
    if (Array.isArray(value)) return value.length === 0
    if (typeof value === 'string') return !value.trim()
    return false
  }


  /**
   * Survey responses with `relates_to` + `relates_to_uuid` set. One row per distinct pair;
   * when duplicates exist, keeps the newest by `completed_at`.
   */
  static findAllWithRelatesToSet() {
    let list = SurveyResponse.store().all().filter(
      (sr) =>
        !SurveyResponse.isEmptyAnswerValue(sr.relates_to) &&
        !SurveyResponse.isEmptyAnswerValue(sr.relates_to_uuid)
    )

    const toTime = (t) => {
      if (t == null) return 0
      return typeof t === 'number' ? t : new Date(t).getTime()
    }
    list = [...list].sort((a, b) => toTime(b.completed_at) - toTime(a.completed_at))

    const seenLink = new Set()
    return list.filter((sr) => {
      const key = `${String(sr.relates_to)}\0${String(sr.relates_to_uuid)}`
      if (seenLink.has(key)) return false
      seenLink.add(key)
      return true
    })
  }

  /**
   * `Field` instances for survey responses whose `relates_to` is `field` (case-insensitive),
   * using the same de-duplicated (`relates_to`, `relates_to_uuid`) list as {@link findAllWithRelatesToSet}.
   * Omits UUIDs not found in the field store.
   */
  static findFieldInstancesFromRelatesToUniqueList() {
    const unique = SurveyResponse.findAllWithRelatesToSet()
    const fields = []
    const seenUuid = new Set()

    for (const sr of unique) {
      if (String(sr.relates_to).trim().toLowerCase() !== 'field') continue
      const uuid = sr.relates_to_uuid
      if (seenUuid.has(uuid)) continue
      seenUuid.add(uuid)

      const field = Field.find(uuid)
      if (!field?.uuid || field.uuid !== uuid) continue
      fields.push(field)
    }

    return fields
  }

  /**
   * Survey response answers for a given question, only from responses that are
   * completed and owned by the customer (completed_by_uuid).
   */
  static findCompletedAnswersForCustomerQuestion(customerUuid, surveyQuestionUuid) {
    const completedResponseUuids = new Set(
      SurveyResponse.store()
        .all()
        .filter(
          (sr) =>
            sr.completed_by_uuid === customerUuid && sr.status === 'completed'
        )
        .map((sr) => sr.uuid)
    )
    return SurveyResponseAnswer.store()
      .all()
      .filter(
        (a) =>
          a.survey_question_uuid === surveyQuestionUuid &&
          completedResponseUuids.has(a.survey_response_uuid)
      )
  }

  /**
   * `Map` from normalized label (`question` trimmed, lowercased) → list of all `SurveyQuestion.uuid`
   * with that exact label (same wording can exist on different surveys).
   */
  static buildSurveyQuestionLabelToUuidsMap() {
    const map = new Map()
    for (const item of SurveyQuestion.store().all()) {
      const label = item.question?.trim().toLowerCase()
      if (!label) continue
      if (!map.has(label)) map.set(label, [])
      map.get(label).push(item.uuid)
    }
    return map
  }

  /**
   * Every question UUID whose `question` text matches `questionLabel` (trim, case-insensitive).
   */
  static resolveAllSurveyQuestionUuidsForLabel(questionLabel) {
    if (questionLabel == null || typeof questionLabel !== 'string') return []
    const needle = questionLabel.trim().toLowerCase()
    if (!needle) return []
    return SurveyQuestion.store()
      .all()
      .filter((item) => item.question?.trim().toLowerCase() === needle)
      .map((item) => item.uuid)
  }

  /**
   * Same as {@link findCompletedAnswersForCustomerQuestion} but accepts a question object
   * or a label string instead of a raw UUID.
   * For a **string** label, includes answers for **every** survey question with that label.
   */
  static findCompletedAnswersForCustomerQuestionByQuestion(customerUuid, questionOrLabel) {
    const uuids =
      typeof questionOrLabel === 'string'
        ? SurveyResponse.resolveAllSurveyQuestionUuidsForLabel(questionOrLabel)
        : questionOrLabel?.uuid
          ? [questionOrLabel.uuid]
          : []

    if (uuids.length === 0) return []

    const uuidSet = new Set(uuids)
    const completedResponseUuids = new Set(
      SurveyResponse.store()
        .all()
        .filter(
          (sr) =>
            sr.completed_by_uuid === customerUuid && sr.status === 'completed'
        )
        .map((sr) => sr.uuid)
    )

    return SurveyResponseAnswer.store()
      .all()
      .filter(
        (a) =>
          uuidSet.has(a.survey_question_uuid) &&
          completedResponseUuids.has(a.survey_response_uuid)
      )
  }

  /**
   * All completed responses with four planting cost fields on the same survey_response_uuid.
   * Missing answers are stored as 0. Newest completed_at first.
   */
  static findAllPlantingCostRowsForCustomer(customerUuid, costQuestionUuids) {
    const {
      seedQuestionUuid,
      fertilizerQuestionUuid,
      laborQuestionUuid,
      fuelQuestionUuid,
    } = costQuestionUuids

    const toPlantingCost = (raw) => {
      if (SurveyResponse.isEmptyAnswerValue(raw)) return 0
      if (typeof raw === 'number' && Number.isFinite(raw)) return raw
      const n = parseFloat(String(raw).replace(/,/g, '').trim())
      return Number.isFinite(n) ? n : 0
    }

    const completed = SurveyResponse.store()
      .all()
      .filter(
        (sr) =>
          sr.completed_by_uuid === customerUuid && sr.status === 'completed'
      )

    const allAnswers = SurveyResponseAnswer.store().all()
    const rows = []

    const answerFor = (responseUuid, questionUuid) =>
      allAnswers.find(
        (a) =>
          a.survey_response_uuid === responseUuid &&
          a.survey_question_uuid === questionUuid
      )

    for (const sr of completed) {
      const rawSeed = answerFor(sr.uuid, seedQuestionUuid)?.answer
      const rawFert = answerFor(sr.uuid, fertilizerQuestionUuid)?.answer
      const rawLabor = answerFor(sr.uuid, laborQuestionUuid)?.answer
      const rawFuel = answerFor(sr.uuid, fuelQuestionUuid)?.answer

      if (
        SurveyResponse.isEmptyAnswerValue(rawSeed) &&
        SurveyResponse.isEmptyAnswerValue(rawFert) &&
        SurveyResponse.isEmptyAnswerValue(rawLabor) &&
        SurveyResponse.isEmptyAnswerValue(rawFuel)
      ) {
        continue
      }

      const cost_of_seed = toPlantingCost(rawSeed)
      const cost_of_fertilizer = toPlantingCost(rawFert)
      const cost_of_labor = toPlantingCost(rawLabor)
      const cost_of_fuel = toPlantingCost(rawFuel)

      rows.push({
        survey_response_uuid: sr.uuid,
        cost_of_seed,
        cost_of_fertilizer,
        cost_of_labor,
        cost_of_fuel,
        completed_at: sr.completed_at,
      })
    }

    rows.sort((a, b) => {
      const toTime = (t) => {
        if (t == null) return 0
        return typeof t === 'number' ? t : new Date(t).getTime()
      }
      return toTime(b.completed_at) - toTime(a.completed_at)
    })

    return rows
  }

  static findDraftByCustomerAndSurvey(customerUuid, surveyUuid) {
    const obj = SurveyResponse.store()
      .all()
      .find(
        (sr) =>
          sr.completed_by_uuid == customerUuid &&
          sr.survey_uuid == surveyUuid &&
          sr.status !== 'completed'
      )
    if (!obj) return null
    const survey_response = new SurveyResponse()
    return Object.assign(survey_response, obj)
  }

  static findContractAssessment(name, uuid, status) {
    const survey = Survey.findByName(name)
    const obj = SurveyResponse.store().all().reverse().find(response => response.survey_uuid == survey.uuid && response.contract_uuid == uuid && response.contract_status == status)
    let survey_response = new SurveyResponse()
    survey_response.contract_uuid ||= uuid
    survey_response.contract_status ||= status
    return Object.assign(survey_response, obj)
  }

  survey_response_answers() {
    let answers = SurveyResponseAnswer.find_all_by_survey_response_uuid(this.uuid)
    let surveyResponseAnswerObjects = []
    answers.forEach(function (item) {
      surveyResponseAnswerObjects.push(Object.assign(new SurveyResponseAnswer(), item))
    })
    return surveyResponseAnswerObjects
  }

  get_customer() {
    let customer = Customer.find(this.completed_by_uuid)
    return customer.name_cache
  }

  get_survey() {
    let survey = Survey.find(this.survey_uuid)
    return survey
  }

  static completed(question_responses) {
    const completed = question_responses.findIndex(question_response => {
      const q = question_response.question
      // Skip compulsory check for follow-up questions that are not activated by the parent's answer
      if (q.condition_question && !q.enabled) return false

      if (
        q.compulsory &&
        SurveyResponse.isEmptyAnswerValue(question_response.response.answer)
      ) {
        return true
      }
      return false
    }) == -1;

    return completed
  }

  async saveResponse(question_responses) {
    const isComplete = SurveyResponse.completed(question_responses)
    this.completed_at = Date.now()
    this.status = isComplete ? 'completed' : 'draft'
    await this.save()

    let responseSavePromises = []

    question_responses.forEach(question_response => {
      let response = question_response.response
      response.survey_response_uuid = this.uuid
      response.survey_question_uuid = question_response.question.uuid
      responseSavePromises.push(response.save())
    })

    return Promise.all(responseSavePromises)
  }
}

useModelName(SurveyResponse, "SurveyResponse")
ModelRegistry.register(SurveyResponse, "SurveyResponse")